import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  BarChart3, TrendingUp, Users, Target, 
  Briefcase, Star, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  in_progress: 'In Progress',
  pending_review: 'Pending Review',
  approved: 'Approved',
  interview_sent: 'Interview Sent',
  interview_confirmed: 'Interview Confirmed',
  interviewed: 'Interviewed',
  hired: 'Hired',
  declined: 'Declined',
  not_moving_forward: 'Not Moving Forward',
};

const STATUS_COLORS_MAP = {
  in_progress: '#9CA3AF',
  pending_review: '#F59E0B',
  approved: '#3B82F6',
  interview_sent: '#8B5CF6',
  interview_confirmed: '#6366F1',
  interviewed: '#06B6D4',
  hired: '#10B981',
  declined: '#EF4444',
  not_moving_forward: '#6B7280',
};

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/analytics/advanced`);
        setData(res.data);
      } catch (error) {
        console.error('Failed to load analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-gray-500">
        <BarChart3 className="h-12 w-12 mx-auto mb-3" />
        <p>Unable to load analytics</p>
      </div>
    );
  }

  const { funnel, score_distribution, source_breakdown, job_stats, rating_distribution, applications_by_day, total_candidates, hired_total } = data;

  const conversionRate = total_candidates > 0 ? ((hired_total / total_candidates) * 100).toFixed(1) : 0;

  const funnelStages = ['pending_review', 'approved', 'interview_confirmed', 'interviewed', 'hired'];
  const maxFunnel = Math.max(...funnelStages.map(s => funnel[s] || 0), 1);

  const maxScore = Math.max(...Object.values(score_distribution), 1);
  const maxRating = Math.max(...Object.values(rating_distribution), 1);

  const sortedDays = Object.entries(applications_by_day).sort((a, b) => a[0].localeCompare(b[0]));
  const maxDayCount = Math.max(...sortedDays.map(([, v]) => v), 1);

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Analytics</h1>
        <p className="text-[#666666] mt-1">Hiring insights and pipeline performance</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-[#4B2D6E]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wide">Total Applicants</p>
                <p className="text-2xl font-bold text-[#333333] mt-1">{total_candidates}</p>
              </div>
              <Users className="h-8 w-8 text-[#4B2D6E] opacity-40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wide">Hired</p>
                <p className="text-2xl font-bold text-[#333333] mt-1">{hired_total}</p>
              </div>
              <Target className="h-8 w-8 text-green-500 opacity-40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-[#E8722A]">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wide">Conversion Rate</p>
                <p className="text-2xl font-bold text-[#333333] mt-1">{conversionRate}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-[#E8722A] opacity-40" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-[#666666] uppercase tracking-wide">Referral vs Direct</p>
                <p className="text-2xl font-bold text-[#333333] mt-1">
                  {source_breakdown.referral} / {source_breakdown.direct}
                </p>
              </div>
              <ArrowUp className="h-8 w-8 text-blue-500 opacity-40" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Hiring Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="pipeline-funnel">
            {funnelStages.map(stage => {
              const count = funnel[stage] || 0;
              const pct = (count / maxFunnel) * 100;
              return (
                <div key={stage} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#666666] w-28 text-right truncate">
                    {STATUS_LABELS[stage]}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                      style={{
                        width: `${Math.max(pct, count > 0 ? 8 : 0)}%`,
                        backgroundColor: STATUS_COLORS_MAP[stage],
                      }}
                    >
                      {count > 0 && (
                        <span className="text-xs font-bold text-white">{count}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 border-t flex items-center gap-3">
              <span className="text-xs font-medium text-[#666666] w-28 text-right">
                Declined
              </span>
              <div className="flex-1">
                <Badge className="bg-red-100 text-red-700 text-xs">
                  {(funnel.declined || 0) + (funnel.not_moving_forward || 0)} declined / not moving forward
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* AI Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              AI Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="score-distribution">
            <div className="flex items-end gap-4 h-40">
              {Object.entries(score_distribution).map(([range, count]) => {
                const pct = (count / maxScore) * 100;
                const colors = {
                  '0-25': 'bg-red-400',
                  '26-50': 'bg-yellow-400',
                  '51-75': 'bg-blue-400',
                  '76-100': 'bg-green-400',
                };
                return (
                  <div key={range} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-bold text-[#333333]">{count}</span>
                    <div className="w-full bg-gray-100 rounded-t-md relative" style={{ height: '120px' }}>
                      <div
                        className={`absolute bottom-0 w-full rounded-t-md transition-all duration-500 ${colors[range]}`}
                        style={{ height: `${Math.max(pct, count > 0 ? 5 : 0)}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#666666] font-medium">{range}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Applications Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Applications (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="applications-timeline">
            {sortedDays.length === 0 ? (
              <div className="h-32 flex items-center justify-center text-sm text-gray-400">
                No applications in the last 30 days
              </div>
            ) : (
              <div className="flex items-end gap-1 h-32 overflow-x-auto">
                {sortedDays.map(([day, count]) => {
                  const pct = (count / maxDayCount) * 100;
                  return (
                    <div key={day} className="flex flex-col items-center gap-1 min-w-[20px] flex-1" title={`${day}: ${count} applications`}>
                      <span className="text-[10px] font-bold text-[#333]">{count}</span>
                      <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '100px' }}>
                        <div
                          className="absolute bottom-0 w-full rounded-t bg-[#4B2D6E] transition-all duration-500"
                          style={{ height: `${Math.max(pct, 5)}%` }}
                        />
                      </div>
                      <span className="text-[9px] text-gray-400 transform -rotate-45 origin-left whitespace-nowrap">
                        {day.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Star Rating Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
              <Star className="h-5 w-5" />
              Owner Rating Distribution
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="rating-distribution">
            <div className="space-y-3">
              {[5, 4, 3, 2, 1].map(rating => {
                const count = rating_distribution[rating] || 0;
                const pct = maxRating > 0 ? (count / maxRating) * 100 : 0;
                return (
                  <div key={rating} className="flex items-center gap-3">
                    <div className="flex items-center gap-0.5 w-20">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star key={i} className={`h-3 w-3 ${i <= rating ? 'fill-[#E8C547] text-[#E8C547]' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[#E8C547] transition-all duration-500"
                        style={{ width: `${Math.max(pct, count > 0 ? 5 : 0)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-[#333] w-8 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Job Performance Table */}
      {job_stats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Job Performance
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="job-performance">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-semibold text-[#4B2D6E]">Job Title</th>
                    <th className="text-right py-2 px-3 font-semibold text-[#4B2D6E]">Applicants</th>
                    <th className="text-right py-2 px-3 font-semibold text-[#4B2D6E]">Hired</th>
                    <th className="text-right py-2 px-3 font-semibold text-[#4B2D6E]">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {job_stats.map(job => (
                    <tr key={job.job_id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`job-stat-${job.job_id}`}>
                      <td className="py-2.5 px-3 font-medium text-[#333]">{job.title}</td>
                      <td className="py-2.5 px-3 text-right text-[#666]">{job.total_applicants}</td>
                      <td className="py-2.5 px-3 text-right">
                        <Badge className="bg-green-100 text-green-700">{job.hired}</Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-medium ${job.conversion_rate > 20 ? 'text-green-600' : 'text-[#666]'}`}>
                          {job.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Source Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
            <Users className="h-5 w-5" />
            Candidate Source
          </CardTitle>
        </CardHeader>
        <CardContent data-testid="source-breakdown">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-[#4B2D6E]" />
              <div>
                <p className="text-sm font-medium text-[#333]">Direct Applications</p>
                <p className="text-2xl font-bold text-[#4B2D6E]">{source_breakdown.direct}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded bg-[#E8722A]" />
              <div>
                <p className="text-sm font-medium text-[#333]">Referrals</p>
                <p className="text-2xl font-bold text-[#E8722A]">{source_breakdown.referral}</p>
              </div>
            </div>
            <div className="flex-1">
              <div className="bg-gray-100 rounded-full h-6 overflow-hidden flex">
                <div
                  className="h-full bg-[#4B2D6E] transition-all"
                  style={{ width: `${total_candidates > 0 ? (source_breakdown.direct / total_candidates) * 100 : 0}%` }}
                />
                <div
                  className="h-full bg-[#E8722A] transition-all"
                  style={{ width: `${total_candidates > 0 ? (source_breakdown.referral / total_candidates) * 100 : 0}%` }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
